import { useEffect, useRef } from 'react'

interface UsePollingOptions {
  interval?: number
  shouldStop?: () => boolean
  shouldStart?: () => boolean
  callback: () => void | Promise<void>
  stopOnSuccess?: boolean
}

export const usePolling = ({
  interval = 3000,
  shouldStart = () => true,
  shouldStop = () => false,
  callback,
  stopOnSuccess = true,
}: UsePollingOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current !== null) {
        return
      }

      const poll = async () => {
        if (shouldStop()) {
          if (stopOnSuccess && intervalRef.current !== null) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }

          return
        }

        await callback()
      }

      intervalRef.current = setInterval(poll, interval)
    }

    const stopPolling = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (shouldStart()) {
          startPolling()
        }

        return
      }

      stopPolling()
    }

    if (document.visibilityState === 'visible' && shouldStart()) {
      startPolling()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPolling()
    }
  }, [interval, shouldStart, shouldStop, callback, stopOnSuccess])
}
